import React, { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { Tag } from 'primereact/tag';
import { exportToExcel } from '../../utilities/exportToExcel';
import {
    cashSalesAssociationState,
    summarizeCashSalesCoverage
} from './cashSalesCoverage';

const STATUS_META = {
    review: {
        label: 'Needs review',
        severity: 'warning',
        icon: 'pi pi-search',
        description: 'No verified change payout or telemetry explanation was found. Service and cashbox collection are context for review, not justification.'
    },
    unconfirmed_zero: {
        label: 'Monitoring',
        severity: 'info',
        icon: 'pi pi-clock',
        description: 'A zero reading is waiting for another report or operational evidence.'
    },
    persistent_zero: {
        label: 'Persistent zero',
        severity: 'info',
        icon: 'pi pi-clock',
        description: 'At least two subsequent reports remain at zero. Treat this as a reporting or machine-data issue until operational evidence is available.'
    },
    expected_service: {
        label: 'Service near decrease',
        severity: 'warning',
        icon: 'pi pi-exclamation-triangle',
        description: 'A registered service is timing context and does not explain a decrease in change tubes.'
    },
    operational_context: {
        label: 'Operational record near decrease',
        severity: 'warning',
        icon: 'pi pi-exclamation-triangle',
        description: 'A route service or consolidation record is context for review and does not explain a decrease in change tubes.'
    },
    documented_collection: {
        label: 'Reported coin collection',
        severity: 'danger',
        icon: 'pi pi-exclamation-triangle',
        description: 'A reported coin collection does not explain a decrease in change tubes and requires review.'
    },
    telemetry_recovery: {
        label: 'Telemetry recovered',
        severity: 'info',
        icon: 'pi pi-refresh',
        description: 'The balance recovered to at least 95% within three subsequent reports or 48 hours, which indicates a temporary reading.'
    },
    data_quality: {
        label: 'Data quality',
        severity: 'info',
        icon: 'pi pi-info-circle',
        description: 'The source reading contains a quality warning and is suppressed from review.'
    },
    insufficient_interval: {
        label: 'Wide interval',
        severity: 'info',
        icon: 'pi pi-clock',
        description: 'The interval between readings is too wide for reliable event timing.'
    },
    data_pending: {
        label: 'Information pending',
        severity: 'warning',
        icon: 'pi pi-hourglass',
        description: 'The service is recent and its consolidation data is not available yet.'
    }
};

const REASON_LABELS = {
    service_time_in_balance_interval: 'Service time inside balance interval',
    service_on_same_panama_date: 'Service on the same Panama date',
    consolidation_on_same_panama_date: 'Consolidation on the same Panama date',
    documented_collection_matches_drop: 'Reported coin collection coincides with decrease',
    cashbox_collection_amount_coincides: 'Reported coin collection coincides with decrease',
    balance_recovered_on_subsequent_report: 'Balance recovered on a subsequent report',
    zero_balance_reading: 'Zero balance reading',
    persistent_zero_reports: 'At least two subsequent zero reports',
    source_data_quality_issue: 'Source data quality issue',
    observation_interval_too_wide: 'Observation interval too wide'
};

const RECONCILIATION_META = {
    explained_change: { label: 'Explained by verified change payout', severity: 'success', description: 'Verified change-dispensing data explains the tube decrease within tolerance.' },
    partial: { label: 'Partially explained by verified change payout', severity: 'warning', description: 'Verified change-dispensing data explains only part of the tube decrease.' },
    telemetry: { label: 'Telemetry recovered', severity: 'info', description: 'The balance recovered on subsequent telemetry, indicating a temporary reading.' },
    unexplained: { label: 'Unexplained tube decrease', severity: 'danger', description: 'No verified change-dispensing data explains the tube decrease. Service and cashbox collection records are context only.' },
    insufficient: { label: 'Insufficient data', severity: 'info', description: 'Source quality or timing prevents a reliable reconciliation.' }
};

const formatMoney = (value) =>
    Number(value || 0).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    });

const toTimestamp = (value) => {
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
};

const uniqueServices = (row) => {
    const seen = new Set();
    return [
        ...(row.service_evidence || []),
        ...(row.same_day_service_evidence || [])
    ].filter((service) => {
        const key = [
            service.service_at,
            service.service_date,
            service.service_time,
            service.action,
            service.driver
        ].join('|');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export const buildObservationTimeline = ({
    observation,
    historyRecords = [],
    beforeHours = 24,
    afterHours = 48
}) => {
    const changedAt = toTimestamp(observation?.observed_at);
    if (changedAt === null) return { readings: [], events: [] };

    const from = changedAt - beforeHours * 60 * 60 * 1000;
    const to = changedAt + afterHours * 60 * 60 * 1000;
    const readings = (Array.isArray(historyRecords) ? historyRecords : [])
        .filter((record) => {
            const timestamp = toTimestamp(record.observed_at);
            return timestamp !== null && timestamp >= from && timestamp <= to;
        })
        .sort((left, right) => toTimestamp(left.observed_at) - toTimestamp(right.observed_at));

    const readingEvents = readings.map((record) => ({
        type: 'reading',
        timestamp: record.observed_at,
        record,
        isChangedReading: toTimestamp(record.observed_at) === changedAt
    }));
    const serviceEvents = uniqueServices(observation)
        .filter((service) => {
            const timestamp = toTimestamp(service.service_at);
            return timestamp !== null && timestamp >= from && timestamp <= to;
        })
        .map((service) => ({
            type: 'service',
            timestamp: service.service_at,
            service
        }));
    const cashSaleEvents = readings
        .filter((record) => {
            const amount = Number(record?.sales_context?.cash_sales);
            return Number.isFinite(amount) && amount > 0 &&
                record?.sales_context?.confidence !== 'ambiguous';
        })
        .map((record) => ({
            type: 'cash_sale',
            timestamp: record.sales_context.dex_reported_at || record.observed_at,
            sale: {
                cash_amount: Number(record.sales_context.cash_sales),
                source: 'DEX',
                confidence: record.sales_context.confidence,
                matching_method: record.sales_context.matching_method
            }
        }));
    const technicianEvents = (observation.syncro_technician_events || [])
        .filter((event) => {
            const timestamp = toTimestamp(event.timestamp);
            return timestamp !== null && timestamp >= from && timestamp <= to;
        })
        .map((event) => ({
            type: event.type === 'ticket_closed'
                ? 'syncro_ticket_closed'
                : 'syncro_technician_assigned',
            timestamp: event.timestamp,
            technician: event
        }));

    return {
        readings,
        events: [...readingEvents, ...serviceEvents, ...cashSaleEvents, ...technicianEvents].sort(
            (left, right) => toTimestamp(left.timestamp) - toTimestamp(right.timestamp)
        )
    };
};

const formatSignedMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '-';
    if (amount === 0) return formatMoney(0);
    return `${amount > 0 ? '+' : '-'}${formatMoney(Math.abs(amount))}`;
};

export const selectObservationsForFilter = ({
    filter,
    observations = [],
    reviewObservations = []
}) => {
    if (filter === 'review') return reviewObservations;
    if (filter === 'all') return observations;
    if (filter === 'monitoring') {
        return observations.filter((observation) =>
            ['unconfirmed_zero', 'persistent_zero'].includes(observation.status)
        );
    }
    if (filter === 'suppressed') {
        return observations.filter(
            (observation) =>
                !['review', 'documented_collection', 'unconfirmed_zero', 'persistent_zero'].includes(observation.status)
        );
    }
    return observations.filter((observation) => observation.status === filter);
};

export const selectPostServiceObservationsForFilter = ({
    filter,
    observations = []
}) => {
    if (filter === 'all') return observations;
    if (filter === 'review') {
        return observations.filter((observation) =>
            ['review', 'documented_collection'].includes(observation.status)
        );
    }
    if (filter === 'monitoring') {
        return observations.filter((observation) =>
            ['unconfirmed_zero', 'persistent_zero'].includes(observation.status)
        );
    }
    return observations.filter((observation) => observation.status === filter);
};

export const observationEvidenceLabel = (row = {}, analysisMode = 'balance') => {
    if (row.status === 'data_pending') return 'Consolidation pending';
    if (row.recovered_on_subsequent_report || row.recovered_on_next_report) {
        return 'Recovered on subsequent report';
    }
    if (row.documented_collection_match) {
        return 'Reported coin collection — requires review';
    }
    if (analysisMode === 'post_service' && row.status === 'review') {
        return 'No verified change payout data';
    }
    if (row.service_evidence?.length) return 'Exact service time';
    if (row.same_day_service_evidence?.length || row.consolidation_evidence?.length) {
        return 'Same-day operational record';
    }
    if (row.quality_issues?.length) return 'Source quality warning';
    return 'No operational evidence';
};

export const observationReconciliation = (row = {}) => {
    if (
        row.status === 'data_quality' ||
        row.status === 'insufficient_interval' ||
        (row.quality_issues || []).length > 0 ||
        !row.previous_observed_at ||
        !row.observed_at
    ) return 'insufficient';
    if (
        row.recovered_on_subsequent_report ||
        row.recovered_on_next_report ||
        row.recovery
    ) return 'telemetry';
    const drop = Math.max(0, Number(row.drop_value || 0));
    const rawVerifiedPayout = [
        row.verified_change_dispensed,
        row.change_payout_amount,
        row.change_payout_evidence?.amount
    ].find((value) => value !== null && value !== undefined && Number.isFinite(Number(value)));
    const verifiedPayout = rawVerifiedPayout === undefined
        ? null
        : Math.max(0, Number(rawVerifiedPayout));
    const tolerance = Math.max(0.05, drop * 0.02);
    if (verifiedPayout !== null && Math.max(0, drop - verifiedPayout) <= tolerance) {
        return 'explained_change';
    }
    if (verifiedPayout > 0) return 'partial';
    return 'unexplained';
};

export const observationReconciliationAmounts = (row = {}) => {
    const drop = Math.max(0, Number(row.drop_value || 0));
    const rawVerifiedPayout = [
        row.verified_change_dispensed,
        row.change_payout_amount,
        row.change_payout_evidence?.amount
    ].find((value) => value !== null && value !== undefined && Number.isFinite(Number(value)));
    const verifiedPayout = rawVerifiedPayout === undefined
        ? null
        : Math.max(0, Number(rawVerifiedPayout));
    const residual = verifiedPayout === null ? drop : Math.max(0, drop - verifiedPayout);
    return {
        verifiedPayout,
        residual,
        explainedPercent: drop > 0 && verifiedPayout !== null
            ? Math.min(100, Number(((verifiedPayout / drop) * 100).toFixed(1)))
            : 0
    };
};

export const observationPeople = (row = {}) => [
    row.operator,
    ...(row.syncro_technician_events || []).map((event) => event.technician_name)
].filter(Boolean);

export const buildObservationEvidenceProfile = (row = {}) => {
    const exactService = Boolean(row.event_at) || (row.service_evidence || []).length > 0;
    const sameDayService = (row.same_day_service_evidence || []).length > 0;
    return [
        { key: 'readings', label: 'Coin readings', available: Boolean(row.previous_observed_at && row.observed_at) },
        { key: 'tubes', label: 'Tube denominations', available: (row.tubes || []).length > 0 },
        { key: 'service', label: 'Exact service record', available: exactService, partial: !exactService && sameDayService },
        { key: 'consolidation', label: 'Consolidation record', available: (row.consolidation_evidence || []).length > 0 },
        { key: 'collection', label: 'Reported coin collection record', available: Boolean(row.documented_collection_match) },
        { key: 'change_payout', label: 'Verified change payout', available: observationReconciliationAmounts(row).verifiedPayout !== null },
        { key: 'recovery', label: 'Subsequent telemetry', available: Boolean(row.recovery || row.recovered_on_subsequent_report || row.recovered_on_next_report) },
        { key: 'cash_sequence', label: 'Cash event sequence', available: false }
    ];
};

export const applyObservationManagementFilters = ({ observations = [], reconciliation = 'all', route = null, operator = null, evidence = 'all', minDrop = 0 }) => {
    const filtered = observations.filter((row) => {
        if (reconciliation !== 'all' && observationReconciliation(row) !== reconciliation) return false;
        if (route && row.route !== route) return false;
        if (operator && !observationPeople(row).includes(operator)) return false;
        if (Number(row.drop_value || 0) < Number(minDrop || 0)) return false;
        if (evidence === 'exact_service' && !(row.event_at || row.service_evidence?.length)) return false;
        if (evidence === 'consolidation' && !row.consolidation_evidence?.length) return false;
        if (evidence === 'telemetry_recovery' && !(row.recovery || row.recovered_on_subsequent_report || row.recovered_on_next_report)) return false;
        if (evidence === 'no_operational_evidence' && observationEvidenceLabel(row) !== 'No operational evidence') return false;
        return true;
    });
    const priority = { unexplained: 0, partial: 1, insufficient: 2, telemetry: 3, explained_change: 4 };
    return [...filtered].sort((left, right) => {
        const statusDifference = priority[observationReconciliation(left)] - priority[observationReconciliation(right)];
        return statusDifference || Number(right.drop_value || 0) - Number(left.drop_value || 0);
    });
};

export const formatObservationTubeLevels = (tubes = []) =>
    (Array.isArray(tubes) ? tubes : [])
        .map((tube) => {
            const name = String(tube.tube || '-').toUpperCase();
            const count = Number(tube.count || 0);
            const denomination = Number(tube.denomination || 0).toFixed(2);
            const total = Number(tube.total || 0).toFixed(2);
            return `${name}: ${count} × $${denomination} = $${total}`;
        })
        .join(' · ');

export const buildObservationExportRows = ({
    observations = [],
    analysisMode = 'balance',
    translate = (value) => value,
    formatDateTime = (value) => value || '-'
}) =>
    observations.map((row) => {
        const meta = STATUS_META[row.status] || STATUS_META.data_quality;
        const match = row.documented_collection_match || null;
        const reconciliationAmounts = observationReconciliationAmounts(row);
        const consolidations = Array.isArray(row.consolidation_evidence)
            ? row.consolidation_evidence
            : [];
        const transactionIds = [
            match?.transaction_id,
            ...consolidations.map((record) => record.transaction_id)
        ].filter((value, index, values) =>
            value !== null &&
            value !== undefined &&
            value !== '' &&
            values.indexOf(value) === index
        );
        const recovered =
            row.recovered_on_subsequent_report ||
            row.recovered_on_next_report ||
            Boolean(row.recovery);

        return {
            classification: translate(meta.label),
            machine: row.machine_name || translate('Unnamed Machine'),
            location: row.location || '',
            system_id: row.system_id ?? '',
            asset: row.asset || '',
            route: row.route || '',
            operator: row.operator || '',
            action: row.action || '',
            service_at:
                analysisMode === 'post_service' ? formatDateTime(row.event_at) : '',
            changed_reading_at: formatDateTime(row.observed_at),
            first_report_at:
                analysisMode === 'post_service'
                    ? formatDateTime(row.observed_at)
                    : '',
            minutes_after_service:
                analysisMode === 'post_service'
                    ? Number(row.minutes_after_event || 0)
                    : null,
            previous_reading_at: formatDateTime(row.previous_observed_at),
            previous_balance: Number(row.previous_total_value || 0),
            balance_after: Number(row.total_value || 0),
            decrease: Number(row.drop_value || 0),
            decrease_percent: Number(row.drop_percent || 0),
            coins_before: Number(row.previous_coin_count || 0),
            coins_after: Number(row.total_coin_count || 0),
            coin_decrease: Number(row.drop_coin_count || 0),
            tube_levels_after: formatObservationTubeLevels(row.tubes),
            reconciliation: translate(RECONCILIATION_META[observationReconciliation(row)].label),
            reconciliation_detail: translate(RECONCILIATION_META[observationReconciliation(row)].description),
            evidence: translate(observationEvidenceLabel(row, analysisMode)),
            exact_service_record: translate((Boolean(row.event_at) || row.service_evidence?.length) ? 'Yes' : 'No'),
            consolidation_record: translate(row.consolidation_evidence?.length ? 'Yes' : 'No'),
            cashbox_collection_context: translate(row.documented_collection_match ? 'Yes' : 'No'),
            subsequent_telemetry_recovery: translate((row.recovery || row.recovered_on_subsequent_report || row.recovered_on_next_report) ? 'Yes' : 'No'),
            cash_event_sequence_available: translate('No'),
            cashbox_collection_amount:
                match?.matched_amount ?? null,
            verified_change_dispensed: reconciliationAmounts.verifiedPayout,
            unexplained_residual: reconciliationAmounts.residual,
            explained_percent: reconciliationAmounts.explainedPercent,
            consolidation_transaction: transactionIds.join(', '),
            recovered: translate(recovered ? 'Yes' : 'No'),
            recovery_at: recovered
                ? formatDateTime(row.recovery?.observed_at)
                : '',
            recovery_balance:
                recovered && row.recovery?.total_value !== undefined
                    ? Number(row.recovery.total_value)
                    : null,
            quality_issues: (row.quality_issues || []).join(', '),
            reasons: (row.reason_codes || [])
                .map((reason) => translate(REASON_LABELS[reason] || reason))
                .join('; '),
            interpretation: translate(meta.description),
            operator_attribution: translate('Not inferred automatically'),
            observation_id: row.observation_key || ''
        };
    });

export const buildObservationExportFilename = ({
    analysisMode = 'balance',
    filter = 'all',
    window = {}
}) => {
    const mode =
        analysisMode === 'post_service'
            ? 'despues-servicio'
            : 'disminuciones-generales';
    const from = String(window.from_date || window.from || 'inicio').slice(0, 10);
    const to = String(window.to_date || window.to || 'fin').slice(0, 10);
    const safeFilter = String(filter || 'all')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    return `parlevel-${mode}-${from}-a-${to}-${safeFilter || 'all'}`;
};

const CoinObservationsPanel = ({
    data,
    reviewData,
    postServiceData,
    postServiceLoading = false,
    onLoadPostService,
    loading = false,
    analysisMode = 'balance',
    onAnalysisModeChange,
    appT = (value) => value,
    preferredLanguage = 'es',
    onFocusMachine,
    historyBySystemId = {},
    onLoadHistory
}) => {
    const [open, setOpen] = useState(true);
    const [filter, setFilter] = useState('review');
    const [expandedRows, setExpandedRows] = useState(null);
    const [reconciliationFilter, setReconciliationFilter] = useState('all');
    const [routeFilter, setRouteFilter] = useState(null);
    const [operatorFilter, setOperatorFilter] = useState(null);
    const [evidenceFilter, setEvidenceFilter] = useState('all');
    const [minDropFilter, setMinDropFilter] = useState(0);

    const observations = useMemo(
        () => (Array.isArray(data?.observations) ? data.observations : []),
        [data]
    );
    const reviewObservations = useMemo(
        () => (Array.isArray(reviewData?.observations) ? reviewData.observations : []),
        [reviewData]
    );
    const postServiceObservations = useMemo(
        () => (Array.isArray(postServiceData?.observations) ? postServiceData.observations : []),
        [postServiceData]
    );
    const activeData = analysisMode === 'post_service' ? postServiceData : data;
    const activeLoading = loading || (analysisMode === 'post_service' && postServiceLoading);
    const summary = activeData?.summary || {};
    const byStatus = summary.by_status || {};

    const explainedCount =
        Number(byStatus.telemetry_recovery || 0) +
        Number(byStatus.data_quality || 0) +
        Number(byStatus.insufficient_interval || 0);

    const analysisOptions = [
        { label: appT('Balance decreases'), value: 'balance', icon: 'pi pi-chart-line' },
        { label: appT('After service'), value: 'post_service', icon: 'pi pi-wrench' }
    ];
    const formatDateOnly = (value) => {
        if (!value) return '-';
        const normalized = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
            ? new Date(`${value}T12:00:00`)
            : new Date(value);
        return normalized.toLocaleDateString(
            preferredLanguage === 'es' ? 'es-PA' : 'en-US',
            {
                timeZone: 'America/Panama',
                dateStyle: 'medium'
            }
        );
    };

    const filterOptions = analysisMode === 'post_service'
        ? [
            { label: appT('Needs review'), value: 'review' },
            { label: appT('Telemetry recovered'), value: 'telemetry_recovery' },
            { label: appT('Monitoring'), value: 'monitoring' },
            { label: appT('All observations'), value: 'all' }
        ]
        : [
            { label: appT('Needs review'), value: 'review' },
            { label: appT('Monitoring'), value: 'monitoring' },
            { label: appT('Explained or suppressed'), value: 'suppressed' },
            { label: appT('All observations'), value: 'all' }
        ];

    const classifiedObservations = useMemo(
        () => analysisMode === 'post_service'
                ? selectPostServiceObservationsForFilter({
                    filter,
                    observations: postServiceObservations
                })
                : selectObservationsForFilter({
                    filter,
                    observations,
                    reviewObservations
                }),
        [analysisMode, filter, observations, postServiceObservations, reviewObservations]
    );

    const routeOptions = useMemo(
        () => [...new Set(classifiedObservations.map((row) => row.route).filter(Boolean))]
            .sort((left, right) => left.localeCompare(right))
            .map((value) => ({ label: value, value })),
        [classifiedObservations]
    );
    const operatorOptions = useMemo(
        () => [...new Set(classifiedObservations.flatMap(observationPeople))]
            .sort((left, right) => left.localeCompare(right))
            .map((value) => ({ label: value, value })),
        [classifiedObservations]
    );
    const filtered = useMemo(
        () => applyObservationManagementFilters({
            observations: classifiedObservations,
            reconciliation: reconciliationFilter,
            route: routeFilter,
            operator: operatorFilter,
            evidence: evidenceFilter,
            minDrop: minDropFilter
        }),
        [classifiedObservations, evidenceFilter, minDropFilter, operatorFilter, reconciliationFilter, routeFilter]
    );

    const formatDateTime = (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleString(
            preferredLanguage === 'es' ? 'es-PA' : 'en-US',
            {
                timeZone: 'America/Panama',
                dateStyle: 'medium',
                timeStyle: 'short'
            }
        );
    };

    const statusBody = (row) => {
        const meta = STATUS_META[row.status] || STATUS_META.data_quality;
        return (
            <Tag
                value={appT(meta.label)}
                severity={meta.severity}
                icon={meta.icon}
            />
        );
    };

    const machineBody = (row) => (
        <div>
            <Button
                label={row.machine_name || appT('Unnamed Machine')}
                className="p-button-link p-0 font-semibold"
                onClick={() => onFocusMachine?.(row)}
            />
            <small className="block text-color-secondary">
                {row.location || '-'} · ID {row.system_id}
            </small>
        </div>
    );

    const decreaseBody = (row) => (
        <div>
            <div className="font-semibold text-orange-600">
                -{formatMoney(row.drop_value)}
            </div>
            <small className="text-color-secondary">
                {Number(row.drop_percent || 0).toFixed(1)}%
            </small>
        </div>
    );

    const evidenceBody = (row) => {
        return appT(observationEvidenceLabel(row, analysisMode));
    };

    const reconciliationBody = (row) => {
        const reconciliation = RECONCILIATION_META[observationReconciliation(row)];
        return <Tag value={appT(reconciliation.label)} severity={reconciliation.severity} title={appT(reconciliation.description)} />;
    };

    const exportColumns = useMemo(() => {
        const commonStart = [
            { key: 'classification', header: appT('Classification'), width: 24 },
            { key: 'machine', header: appT('Machine'), width: 32 },
            { key: 'location', header: appT('Location'), width: 34 },
            { key: 'system_id', header: appT('Machine ID'), width: 12 },
            { key: 'asset', header: appT('Asset'), width: 16 },
            { key: 'route', header: appT('Route'), width: 18 }
        ];
        const serviceColumns = analysisMode === 'post_service'
            ? [
                { key: 'operator', header: appT('Operator'), width: 24 },
                { key: 'action', header: appT('Action'), width: 22 },
                { key: 'service_at', header: appT('Service date and time'), width: 22 },
                { key: 'first_report_at', header: appT('First report date and time'), width: 22 },
                { key: 'minutes_after_service', header: appT('Minutes after service'), width: 18 }
            ]
            : [
                { key: 'changed_reading_at', header: appT('Changed reading'), width: 22 }
            ];
        return [
            ...commonStart,
            ...serviceColumns,
            { key: 'previous_reading_at', header: appT('Previous reading'), width: 22 },
            { key: 'previous_balance', header: appT('Balance before'), width: 16 },
            { key: 'balance_after', header: appT('Balance after'), width: 16 },
            { key: 'decrease', header: appT('Decrease'), width: 14 },
            { key: 'decrease_percent', header: appT('Decrease percent'), width: 14 },
            { key: 'coins_before', header: appT('Coins before'), width: 14 },
            { key: 'coins_after', header: appT('Coins after'), width: 14 },
            { key: 'coin_decrease', header: appT('Coin decrease'), width: 14 },
            { key: 'tube_levels_after', header: appT('Tube levels after'), width: 48 },
            { key: 'reconciliation', header: appT('Reconciliation'), width: 22 },
            { key: 'reconciliation_detail', header: appT('Reconciliation detail'), width: 58 },
            { key: 'evidence', header: appT('Evidence'), width: 34 },
            { key: 'exact_service_record', header: appT('Exact service record'), width: 18 },
            { key: 'consolidation_record', header: appT('Consolidation record'), width: 18 },
            { key: 'verified_change_dispensed', header: appT('Verified change dispensed'), width: 20 },
            { key: 'unexplained_residual', header: appT('Unexplained residual'), width: 20 },
            { key: 'explained_percent', header: appT('Explained percent'), width: 18 },
            { key: 'cashbox_collection_context', header: appT('Reported coin collection'), width: 22 },
            { key: 'subsequent_telemetry_recovery', header: appT('Subsequent telemetry'), width: 20 },
            { key: 'cash_event_sequence_available', header: appT('Cash event sequence'), width: 20 },
            { key: 'cashbox_collection_amount', header: appT('Reported coin collection amount'), width: 22 },
            { key: 'consolidation_transaction', header: appT('Consolidation transaction'), width: 24 },
            { key: 'recovered', header: appT('Recovered'), width: 12 },
            { key: 'recovery_at', header: appT('Recovery date and time'), width: 22 },
            { key: 'recovery_balance', header: appT('Recovery balance'), width: 16 },
            { key: 'quality_issues', header: appT('Quality issues'), width: 30 },
            { key: 'reasons', header: appT('Reason codes'), width: 46 },
            { key: 'interpretation', header: appT('Interpretation'), width: 64 },
            { key: 'operator_attribution', header: appT('Operator attribution'), width: 26 },
            { key: 'observation_id', header: appT('Observation ID'), width: 42 }
        ];
    }, [analysisMode, appT]);

    const handleExportExcel = () => {
        const exportRows = buildObservationExportRows({
            observations: filtered,
            analysisMode,
            translate: appT,
            formatDateTime
        });
        exportToExcel(
            exportRows,
            buildObservationExportFilename({
                analysisMode,
                filter,
                window: activeData?.window || {}
            }),
            exportColumns
        );
    };

    const expansionTemplate = (row) => {
        const meta = STATUS_META[row.status] || STATUS_META.data_quality;
        const reconciliationMeta = RECONCILIATION_META[observationReconciliation(row)];
        const reconciliationAmounts = observationReconciliationAmounts(row);
        const evidenceProfile = buildObservationEvidenceProfile(row);
        const services = uniqueServices(row);
        const historyState =
            historyBySystemId[row.observation_key || String(row.system_id)] || {};
        const timeline = buildObservationTimeline({
            observation: row,
            historyRecords: historyState.records || []
        });
        const salesCoverage = summarizeCashSalesCoverage(timeline.readings);
        const recoveryTimestamp = toTimestamp(row.recovery?.observed_at);
        const changedRecord = timeline.readings.find(
            (record) => toTimestamp(record.observed_at) === toTimestamp(row.observed_at)
        );
        const changedTubes = (changedRecord?.tube_deltas || []).filter(
            (tube) => Number(tube.delta_count || 0) !== 0
        );
        const tubeNetValue = changedTubes.reduce(
            (total, tube) => total + Number(tube.delta_count || 0) * Number(tube.denomination || 0),
            0
        );
        const chartData = {
            labels: timeline.readings.map((record) => formatDateTime(record.observed_at)),
            datasets: [
                {
                    label: appT('Coin balance'),
                    data: timeline.readings.map((record) => Number(record.total_value || 0)),
                    borderColor: '#3B82F6',
                    backgroundColor: 'rgba(59, 130, 246, 0.12)',
                    fill: true,
                    tension: 0.15,
                    pointRadius: timeline.readings.map((record) => {
                        const timestamp = toTimestamp(record.observed_at);
                        if (timestamp === toTimestamp(row.observed_at)) return 6;
                        if (recoveryTimestamp !== null && timestamp === recoveryTimestamp) return 6;
                        return 4;
                    }),
                    pointBackgroundColor: timeline.readings.map((record) => {
                        const timestamp = toTimestamp(record.observed_at);
                        if (timestamp === toTimestamp(row.observed_at)) return '#F97316';
                        if (recoveryTimestamp !== null && timestamp === recoveryTimestamp) return '#22C55E';
                        return '#3B82F6';
                    })
                }
            ]
        };
        const chartOptions = {
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (context) => `${appT('Coin balance')}: ${formatMoney(context.parsed.y)}`
                    }
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {
                        callback: (value) => formatMoney(value)
                    }
                },
                x: {
                    ticks: {
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: 8
                    }
                }
            }
        };

        const tubeDetail = (record) => {
            const tubes = (record.tubes || [])
                .map((tube) => `${String(tube.tube || '').replace('TUBE_', '')}: ${tube.count}`)
                .join(' · ');
            const changes = (record.tube_deltas || [])
                .filter((tube) => Number(tube.delta_count || 0) !== 0)
                .map((tube) => {
                    const count = Number(tube.delta_count || 0);
                    return `${String(tube.tube || '').replace('TUBE_', '')}: ${count > 0 ? '+' : ''}${count}`;
                })
                .join(' · ');
            return (
                <div>
                    <div>{tubes || '-'}</div>
                    {changes && (
                        <small className="text-color-secondary">
                            {appT('Tube changes')}: {changes}
                        </small>
                    )}
                </div>
            );
        };

        return (
            <div className="p-3 surface-50">
                <div className="grid">
                    <div className="col-6 md:col-3">
                        <small className="text-color-secondary">{appT('Balance before')}</small>
                        <div className="text-xl font-semibold">
                            {formatMoney(row.previous_total_value)}
                        </div>
                        <small>{Number(row.previous_coin_count || 0).toLocaleString()} {appT('coins')}</small>
                    </div>
                    <div className="col-6 md:col-3">
                        <small className="text-color-secondary">{appT('Balance after')}</small>
                        <div className="text-xl font-semibold">{formatMoney(row.total_value)}</div>
                        <small>{Number(row.total_coin_count || 0).toLocaleString()} {appT('coins')}</small>
                    </div>
                    <div className="col-6 md:col-3">
                        <small className="text-color-secondary">{appT('Decrease')}</small>
                        <div className="text-xl font-semibold text-orange-600">
                            -{formatMoney(row.drop_value)}
                        </div>
                        <small>{Number(row.drop_percent || 0).toFixed(1)}%</small>
                    </div>
                    <div className="col-6 md:col-3">
                        <small className="text-color-secondary">
                            {analysisMode === 'post_service'
                                ? appT('First report after service')
                                : appT('Reading interval')}
                        </small>
                        <div className="text-xl font-semibold">
                            {analysisMode === 'post_service'
                                ? `${Number(row.minutes_after_event || 0).toFixed(1)} min`
                                : `${Number(row.interval_hours || 0).toFixed(1)}h`}
                        </div>
                        <small>{appT('Panama time')}</small>
                    </div>
                </div>

                <div className="surface-card border-1 surface-border border-round p-3 mt-3">
                    <div className="flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                        <div>
                            <div className="font-semibold">{appT('Interval reconciliation')}</div>
                            <small className="text-color-secondary">
                                {appT('This summarizes available evidence; it does not identify transaction order or responsibility.')}
                            </small>
                        </div>
                        <Tag value={appT(reconciliationMeta.label)} severity={reconciliationMeta.severity} />
                    </div>
                    <div className="grid">
                        <div className="col-6 md:col-3">
                            <small className="text-color-secondary">{appT('Coin fund variation')}</small>
                            <div className="font-semibold text-orange-600">-{formatMoney(row.drop_value)}</div>
                        </div>
                        <div className="col-6 md:col-3">
                            <small className="text-color-secondary">{appT('Verified change dispensed')}</small>
                            <div className="font-semibold">
                                {reconciliationAmounts.verifiedPayout === null
                                    ? appT('Not available')
                                    : formatMoney(reconciliationAmounts.verifiedPayout)}
                            </div>
                        </div>
                        <div className="col-6 md:col-3">
                            <small className="text-color-secondary">{appT('Net tube variation')}</small>
                            <div className="font-semibold">
                                {historyState.loading || !historyState.loaded
                                    ? appT('Loading detailed history')
                                    : changedTubes.length ? formatSignedMoney(tubeNetValue) : appT('No tube changes reported')}
                            </div>
                        </div>
                        <div className="col-6 md:col-3">
                            <small className="text-color-secondary">{appT('Unexplained residual')}</small>
                            <div className="font-semibold text-red-500">
                                {formatMoney(reconciliationAmounts.residual)}
                            </div>
                        </div>
                    </div>
                    {changedTubes.length > 0 && (
                        <div className="text-sm mt-2">
                            <strong>{appT('Tube changes')}:</strong>{' '}
                            {changedTubes.map((tube) => {
                                const count = Number(tube.delta_count || 0);
                                return `${String(tube.tube || '').replace('TUBE_', '')}: ${count > 0 ? '+' : ''}${count} × ${formatMoney(tube.denomination)}`;
                            }).join(' · ')}
                        </div>
                    )}
                    {row.documented_collection_match && (
                        <div className="text-sm mt-2 text-red-500">
                            <strong>{appT('Reported coin collection')}:</strong>{' '}
                            {formatMoney(row.documented_collection_match.matched_amount)}.{' '}
                            {appT('This does not explain a decrease in change tubes and requires review.')}
                        </div>
                    )}
                    <div className="text-sm mt-2 text-color-secondary">{appT(reconciliationMeta.description)}</div>
                </div>

                <div className="grid mt-2">
                    <div className="col-12">
                        <div className="flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                            <div>
                                <div className="font-semibold">{appT('Detailed timeline')}</div>
                                <small className="text-color-secondary">
                                    {appT('24 hours before and 48 hours after the changed reading')} · {appT('Panama time')}
                                </small>
                                {historyState.loaded && salesCoverage.total > 0 && (
                                    <small className="block mt-1 text-color-secondary">
                                        {appT('Cash sales associated')}: {salesCoverage.associated} / {salesCoverage.total} ({salesCoverage.percent}%)
                                        {salesCoverage.missing > 0 && ` · ${appT('Sales data not associated')}: ${salesCoverage.missing}`}
                                        {salesCoverage.ambiguous > 0 && ` · ${appT('Ambiguous sales data')}: ${salesCoverage.ambiguous}`}
                                    </small>
                                )}
                            </div>
                            <div className="flex gap-3 text-sm">
                                <span><i className="pi pi-circle-fill text-xs text-primary mr-1" />{appT('Reading')}</span>
                                <span><i className="pi pi-circle-fill text-xs text-orange-500 mr-1" />{appT('Changed reading')}</span>
                                <span><i className="pi pi-circle-fill text-xs text-green-500 mr-1" />{appT('Recovery reading')}</span>
                                <span><i className="pi pi-circle-fill text-xs text-blue-500 mr-1" />{appT('Cash sale')}</span>
                                <span><i className="pi pi-circle-fill text-xs text-purple-500 mr-1" />{appT('Syncro technical activity')}</span>
                            </div>
                        </div>

                        {historyState.loading ? (
                            <div className="flex align-items-center justify-content-center gap-2 py-5 text-color-secondary">
                                <i className="pi pi-spin pi-spinner" />
                                {appT('Loading detailed history')}
                            </div>
                        ) : historyState.error ? (
                            <div className="p-3 border-round surface-100 text-orange-700 flex justify-content-between align-items-center gap-2">
                                <span>
                                    <i className="pi pi-exclamation-triangle mr-2" />
                                    {historyState.error}
                                </span>
                                <Button
                                    label={appT('Try again')}
                                    icon="pi pi-refresh"
                                    className="p-button-outlined p-button-sm"
                                    onClick={() => onLoadHistory?.(row)}
                                />
                            </div>
                        ) : timeline.readings.length ? (
                            <>
                                <div style={{ height: '240px' }}>
                                    <Chart type="line" data={chartData} options={chartOptions} />
                                </div>
                                <div className="overflow-auto mt-3">
                                    <table className="w-full text-sm" style={{ borderCollapse: 'collapse', minWidth: '900px' }}>
                                        <thead>
                                            <tr className="border-bottom-1 surface-border text-left">
                                                <th className="p-2">{appT('Date and time')}</th>
                                                <th className="p-2">{appT('Event')}</th>
                                                <th className="p-2">{appT('Coin balance')}</th>
                                                <th className="p-2">{appT('Variation')}</th>
                                                <th className="p-2">{appT('Coins')}</th>
                                                <th className="p-2">{appT('Tubes and changes')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {timeline.events.map((event, index) => {
                                                if (event.type === 'service') {
                                                    return (
                                                        <tr className="border-bottom-1 surface-border" key={`service-${event.timestamp}-${index}`}>
                                                            <td className="p-2">{formatDateTime(event.timestamp)}</td>
                                                            <td className="p-2" colSpan={5}>
                                                                <Tag value={appT('Registered service')} severity="success" icon="pi pi-wrench" />
                                                                <span className="ml-2">
                                                                    {event.service.action || ''}
                                                                    {event.service.driver ? ` · ${event.service.driver}` : ''}
                                                                    {event.service.route_name ? ` · ${event.service.route_name}` : ''}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                                if (event.type === 'cash_sale') {
                                                    return (
                                                        <tr className="border-bottom-1 surface-border bg-blue-50" key={`cash-sale-${event.timestamp}-${index}`}>
                                                            <td className="p-2">{formatDateTime(event.timestamp)}</td>
                                                            <td className="p-2">
                                                                <Tag value={appT('Cash sale')} severity="info" icon="pi pi-dollar" />
                                                                <span className="ml-2 font-semibold">{formatMoney(event.sale.cash_amount)}</span>
                                                                <small className="ml-2 text-color-secondary">
                                                                    DEX · {appT(event.sale.confidence === 'hourly' ? 'Hourly association' : 'Exact association')}
                                                                </small>
                                                            </td>
                                                            <td className="p-2 text-color-secondary">-</td>
                                                            <td className="p-2 text-color-secondary">-</td>
                                                            <td className="p-2 text-color-secondary">-</td>
                                                            <td className="p-2 text-color-secondary">
                                                                {appT('The sale does not confirm how the coin tubes changed.')}
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                                if (event.type === 'syncro_ticket_closed' || event.type === 'syncro_technician_assigned') {
                                                    const isClosed = event.type === 'syncro_ticket_closed';
                                                    const repairOrderUrl = event.technician.repair_order_id
                                                        ? `/#/repair-orders/${event.technician.repair_order_id}`
                                                        : null;
                                                    return (
                                                        <tr className="border-bottom-1 surface-border bg-purple-50" key={`${event.type}-${event.timestamp}-${index}`}>
                                                            <td className="p-2">{formatDateTime(event.timestamp)}</td>
                                                            <td className="p-2">
                                                                <Tag
                                                                    value={appT(isClosed ? 'Technical ticket closed' : 'Technician assigned')}
                                                                    severity={isClosed ? 'success' : 'info'}
                                                                    icon={isClosed ? 'pi pi-check-circle' : 'pi pi-user-plus'}
                                                                />
                                                                <span className="ml-2 font-semibold">{event.technician.technician_name}</span>
                                                                {repairOrderUrl && (
                                                                    <a
                                                                        className="ml-2"
                                                                        href={repairOrderUrl}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        {event.technician.ticket_code || appT('Syncro ticket')}
                                                                    </a>
                                                                )}
                                                            </td>
                                                            <td className="p-2 text-color-secondary">-</td>
                                                            <td className="p-2 text-color-secondary">-</td>
                                                            <td className="p-2 text-color-secondary">-</td>
                                                            <td className="p-2 text-color-secondary">
                                                                {appT(isClosed
                                                                    ? 'Syncro records the ticket as closed at this time; it does not confirm the time of a physical visit.'
                                                                    : 'Assignment in Syncro; physical visit not confirmed.')}
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                                const record = event.record;
                                                const salesState = cashSalesAssociationState(record);
                                                const isRecovery = recoveryTimestamp !== null &&
                                                    toTimestamp(record.observed_at) === recoveryTimestamp;
                                                return (
                                                    <tr
                                                        className={`border-bottom-1 surface-border ${event.isChangedReading ? 'bg-orange-50' : isRecovery ? 'bg-green-50' : ''}`}
                                                        key={`reading-${record.observed_at}-${index}`}
                                                    >
                                                        <td className="p-2">{formatDateTime(record.observed_at)}</td>
                                                        <td className="p-2">
                                                            {event.isChangedReading ? (
                                                                <Tag value={appT('Changed reading')} severity="warning" />
                                                            ) : isRecovery ? (
                                                                <Tag value={appT('Recovery reading')} severity="success" />
                                                            ) : (
                                                                <Tag value={appT('Reading')} severity="info" />
                                                            )}
                                                            {record.quality_status && record.quality_status !== 'good' && (
                                                                <Tag
                                                                    className="ml-1"
                                                                    value={appT('Quality warning')}
                                                                    severity="warning"
                                                                    title={(record.quality_issues || []).join(', ')}
                                                                />
                                                            )}
                                                            {salesState === 'no_sale' && (
                                                                <small className="block mt-1 text-color-secondary">
                                                                    {appT('No cash sale')}
                                                                </small>
                                                            )}
                                                            {salesState === 'missing' && (
                                                                <small className="block mt-1 text-orange-700">
                                                                    {appT('Sales data not associated')}
                                                                </small>
                                                            )}
                                                            {salesState === 'ambiguous' && (
                                                                <small className="block mt-1 text-orange-700">
                                                                    {appT('Ambiguous sales data')}
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td className="p-2 font-semibold">{formatMoney(record.total_value)}</td>
                                                        <td className={`p-2 font-semibold ${Number(record.delta_value) < 0 ? 'text-orange-600' : Number(record.delta_value) > 0 ? 'text-green-600' : ''}`}>
                                                            {record.delta_value === null || record.delta_value === undefined
                                                                ? '-'
                                                                : formatSignedMoney(record.delta_value)}
                                                        </td>
                                                        <td className="p-2">
                                                            {Number(record.total_coin_count || 0).toLocaleString()}
                                                            {record.delta_coin_count !== null && record.delta_coin_count !== undefined && (
                                                                <small className="block text-color-secondary">
                                                                    {Number(record.delta_coin_count) > 0 ? '+' : ''}{Number(record.delta_coin_count)}
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td className="p-2">{tubeDetail(record)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="p-3 border-round surface-100 text-color-secondary">
                                {historyState.loaded
                                    ? appT('No detailed readings were found in this time window')
                                    : appT('Open the observation to load its detailed history')}
                            </div>
                        )}
                    </div>
                    <div className="col-12 md:col-6 mt-2">
                        <div className="font-semibold mb-2">{appT('Operational evidence')}</div>
                        {services.length > 0 ? (
                            services.map((service, index) => (
                                <div className="mb-2" key={`${service.service_date}-${service.service_time}-${index}`}>
                                    <i className="pi pi-wrench mr-2 text-green-600" />
                                    <strong>{appT('Registered service')}:</strong>{' '}
                                    {service.service_date || '-'} {service.service_time || ''}
                                    {service.action ? ` · ${service.action}` : ''}
                                    {service.driver ? ` · ${service.driver}` : ''}
                                </div>
                            ))
                        ) : (
                            <div className="text-color-secondary">{appT('No registered service in the interval')}</div>
                        )}
                        {(row.consolidation_evidence || []).map((record, index) => (
                            <div className="mb-2" key={`${record.transaction_id}-${index}`}>
                                <i className="pi pi-file mr-2 text-primary" />
                                <strong>{appT('Consolidation')}:</strong>{' '}
                                {record.service_date || '-'}
                                {record.action_type ? ` · ${record.action_type}` : ''}
                                {' · '}{appT('Collected coins')}: {formatMoney(record.collected_coins)}
                            </div>
                        ))}
                        <div className="font-semibold mt-3 mb-2">{appT('Evidence completeness')}</div>
                        <div className="flex flex-wrap gap-2">
                            {evidenceProfile.map((item) => (
                                <Tag
                                    key={item.key}
                                    value={`${item.available ? '✓' : item.partial ? '~' : '—'} ${appT(item.label)}`}
                                    severity={item.available ? 'success' : item.partial ? 'warning' : 'info'}
                                    title={item.key === 'cash_sequence'
                                        ? appT('Parlevel snapshots do not provide the exact order of accepted cash and returned change.')
                                        : undefined}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="col-12 md:col-6 mt-2">
                        <div className="font-semibold mb-2">{appT('Key observation points')}</div>
                        {analysisMode === 'post_service' && row.event_at && (
                            <div className="mb-1">
                                <i className="pi pi-wrench text-green-600 mr-2" />
                                {appT('Registered service')}: {formatDateTime(row.event_at)}
                                {row.operator ? ` · ${row.operator}` : ''}
                            </div>
                        )}
                        <div className="mb-1">
                            <i className="pi pi-circle-fill text-xs text-primary mr-2" />
                            {appT('Previous reading')}: {formatDateTime(row.previous_observed_at)}
                        </div>
                        <div className="mb-1">
                            <i className="pi pi-circle-fill text-xs text-orange-500 mr-2" />
                            {appT('Changed reading')}: {formatDateTime(row.observed_at)}
                        </div>
                        {row.recovery?.observed_at && (
                            <div>
                                <i className="pi pi-circle-fill text-xs text-green-500 mr-2" />
                                {appT('Recovery reading')}: {formatDateTime(row.recovery.observed_at)}
                                {' · '}{formatMoney(row.recovery.total_value)}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 my-2">
                    {(row.reason_codes || []).map((reason) => (
                        <Tag
                            key={reason}
                            value={appT(REASON_LABELS[reason] || reason)}
                            severity="info"
                        />
                    ))}
                </div>
                <div
                    className="p-3 border-round"
                    style={{ background: 'var(--surface-100)', borderLeft: '4px solid var(--orange-400)' }}
                >
                    <strong>{appT(meta.label)}:</strong> {appT(meta.description)}
                    <div className="text-sm mt-1">
                        {appT('A coin observation does not prove coin removal or operator responsibility.')}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Card>
            <div className="flex justify-content-between align-items-start flex-wrap gap-3">
                <div>
                    <div className="text-xl font-semibold">
                        <i className="pi pi-eye mr-2 text-primary" />
                        {appT('Coin observations')}
                    </div>
                    <div className="text-color-secondary mt-1">
                        {appT('Observation mode')} ·{' '}
                        {formatDateOnly(activeData?.window?.from_date || activeData?.window?.from)}
                        {' – '}
                        {formatDateOnly(activeData?.window?.to_date || activeData?.window?.to)}
                    </div>
                    <small className="text-color-secondary">
                        {analysisMode === 'post_service'
                            ? `${appT('Post-service rule')}: ${appT('any decrease')} · ≤15 min · ${appT('first report')}`
                            : `${appT('Telemetry recovery rule')}: ≥95% · ${appT('next three reports')} · 48h`}
                    </small>
                </div>
                <Button
                    label={open ? appT('Hide observations') : appT('View observations')}
                    icon={open ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                    className="p-button-outlined p-button-sm"
                    loading={activeLoading}
                    onClick={() => setOpen((value) => !value)}
                />
            </div>

            <div className="mt-3">
                <SelectButton
                    value={analysisMode}
                    options={analysisOptions}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(event) => {
                        if (!event.value) return;
                        onAnalysisModeChange?.(event.value);
                        setFilter('review');
                        setReconciliationFilter('all');
                        setRouteFilter(null);
                        setOperatorFilter(null);
                        setEvidenceFilter('all');
                        setMinDropFilter(0);
                        setExpandedRows(null);
                        if (event.value === 'post_service') onLoadPostService?.();
                    }}
                />
            </div>

            <div className="grid mt-2">
                <div className="col-6 md:col-3">
                    <div className="surface-50 border-round p-3 h-full">
                        <small className="text-color-secondary">{appT('Needs review')}</small>
                        <div className="text-2xl font-bold text-orange-500">{summary.review || 0}</div>
                    </div>
                </div>
                <div className="col-6 md:col-3">
                    <div className="surface-50 border-round p-3 h-full">
                        <small className="text-color-secondary">
                            {analysisMode === 'post_service' ? appT('Drops after service') : appT('Monitoring')}
                        </small>
                        <div className="text-2xl font-bold text-primary">
                            {analysisMode === 'post_service' ? summary.drops || 0 : summary.watch || 0}
                        </div>
                    </div>
                </div>
                <div className="col-6 md:col-3">
                    <div className="surface-50 border-round p-3 h-full">
                        <small className="text-color-secondary">
                            {analysisMode === 'post_service' ? appT('Telemetry or monitoring') : appT('Explained or suppressed')}
                        </small>
                        <div className="text-2xl font-bold text-green-600">
                            {analysisMode === 'post_service'
                                ? Number(summary.telemetry || 0) + Number(summary.monitoring || 0)
                                : explainedCount}
                        </div>
                    </div>
                </div>
                <div className="col-6 md:col-3">
                    <div className="surface-50 border-round p-3 h-full">
                        <small className="text-color-secondary">
                            {analysisMode === 'post_service' ? appT('Services evaluated') : appT('Evaluated')}
                        </small>
                        <div className="text-2xl font-bold">
                            {analysisMode === 'post_service' ? summary.evaluated_services || 0 : summary.total || 0}
                        </div>
                        {analysisMode === 'post_service' && (
                            <small>{summary.drops || 0} {appT('decreases found')}</small>
                        )}
                    </div>
                </div>
            </div>

            {open && (
                <div className="mt-3">
                    <div
                        className="p-3 border-round mb-3 text-sm"
                        style={{ background: 'var(--surface-100)', borderLeft: '4px solid var(--blue-400)' }}
                    >
                        <i className="pi pi-shield mr-2" />
                        {analysisMode === 'post_service'
                            ? appT('A decrease after service is a temporal correlation, not proof of coin removal or operator responsibility.')
                            : appT('A coin observation does not prove coin removal or operator responsibility.')}
                        {' '}{appT('Automatic alerts are disabled.')}
                    </div>
                    <div className="flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div>
                            <label className="font-semibold mr-2">{appT('Show')}</label>
                            <Dropdown
                                value={filter}
                                options={filterOptions}
                                onChange={(event) => {
                                    setFilter(event.value);
                                    setExpandedRows(null);
                                }}
                                style={{ minWidth: '220px' }}
                            />
                        </div>
                        <div className="flex align-items-center gap-3">
                            <small className="text-color-secondary">
                                {filtered.length} {appT('observations')}
                            </small>
                            <Button
                                label={`${appT('Export to Excel')} (${filtered.length})`}
                                icon="pi pi-file-excel"
                                className="p-button-outlined p-button-success p-button-sm"
                                disabled={activeLoading || filtered.length === 0}
                                title={appT('Export current filtered observations to Excel')}
                                onClick={handleExportExcel}
                            />
                        </div>
                    </div>
                    <div className="grid mb-2">
                        <div className="col-12 md:col-3">
                            <label className="block text-sm font-semibold mb-1">{appT('Reconciliation')}</label>
                            <Dropdown
                                value={reconciliationFilter}
                                options={[
                                    { label: appT('All reconciliation statuses'), value: 'all' },
                                    ...Object.entries(RECONCILIATION_META).map(([value, item]) => ({ value, label: appT(item.label) }))
                                ]}
                                onChange={(event) => { setReconciliationFilter(event.value); setExpandedRows(null); }}
                                className="w-full"
                            />
                        </div>
                        <div className="col-12 md:col-2">
                            <label className="block text-sm font-semibold mb-1">{appT('Route')}</label>
                            <Dropdown value={routeFilter} options={routeOptions} placeholder={appT('All routes')} showClear filter
                                onChange={(event) => { setRouteFilter(event.value || null); setExpandedRows(null); }} className="w-full" />
                        </div>
                        {analysisMode === 'post_service' && (
                            <div className="col-12 md:col-2">
                                <label className="block text-sm font-semibold mb-1">{appT('Operator or technician')}</label>
                                <Dropdown value={operatorFilter} options={operatorOptions} placeholder={appT('All operators and technicians')} showClear filter
                                    onChange={(event) => { setOperatorFilter(event.value || null); setExpandedRows(null); }} className="w-full" />
                            </div>
                        )}
                        <div className="col-12 md:col-3">
                            <label className="block text-sm font-semibold mb-1">{appT('Evidence type')}</label>
                            <Dropdown
                                value={evidenceFilter}
                                options={[
                                    { label: appT('All evidence'), value: 'all' },
                                    { label: appT('Exact service record'), value: 'exact_service' },
                                    { label: appT('Consolidation record'), value: 'consolidation' },
                                    { label: appT('Subsequent telemetry'), value: 'telemetry_recovery' },
                                    { label: appT('No operational evidence'), value: 'no_operational_evidence' }
                                ]}
                                onChange={(event) => { setEvidenceFilter(event.value); setExpandedRows(null); }}
                                className="w-full"
                            />
                        </div>
                        <div className="col-12 md:col-2">
                            <label className="block text-sm font-semibold mb-1">{appT('Minimum decrease')}</label>
                            <Dropdown
                                value={minDropFilter}
                                options={[
                                    { label: appT('Any decrease'), value: 0 },
                                    { label: '≥ $5', value: 5 },
                                    { label: '≥ $10', value: 10 },
                                    { label: '≥ $25', value: 25 },
                                    { label: '≥ $50', value: 50 }
                                ]}
                                onChange={(event) => { setMinDropFilter(event.value); setExpandedRows(null); }}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <small className="block text-color-secondary mb-3">
                        {appT('Management priority shows unexplained observations first, then the largest decreases.')}
                    </small>
                    <DataTable
                        value={filtered}
                        loading={activeLoading}
                        expandedRows={expandedRows}
                        onRowToggle={(event) => setExpandedRows(event.data)}
                        onRowExpand={(event) => onLoadHistory?.(event.data)}
                        rowExpansionTemplate={expansionTemplate}
                        dataKey="observation_key"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        emptyMessage={appT('No observations match this filter')}
                        responsiveLayout="scroll"
                        size="small"
                    >
                        <Column expander style={{ width: '3rem' }} />
                        <Column header={appT('Classification')} body={statusBody} field="status" sortable style={{ minWidth: '165px' }} />
                        <Column header={appT('Reconciliation')} body={reconciliationBody} style={{ minWidth: '175px' }} />
                        <Column header={appT('Machine')} body={machineBody} field="machine_name" sortable style={{ minWidth: '230px' }} />
                        <Column header={appT('Route')} field="route" sortable style={{ minWidth: '130px' }} />
                        {analysisMode === 'post_service' && (
                            <Column
                                header={appT('Operator and action')}
                                body={(row) => (
                                    <div>
                                        <div>{row.operator || '-'}</div>
                                        <small className="text-color-secondary">{row.action || '-'}</small>
                                    </div>
                                )}
                                field="operator"
                                sortable
                                style={{ minWidth: '170px' }}
                            />
                        )}
                        <Column header={appT('Decrease')} body={decreaseBody} field="drop_value" sortable style={{ minWidth: '110px' }} />
                        <Column
                            header={analysisMode === 'post_service' ? appT('Registered service') : appT('Changed reading')}
                            body={(row) => formatDateTime(analysisMode === 'post_service' ? row.event_at : row.observed_at)}
                            field={analysisMode === 'post_service' ? 'event_at' : 'observed_at'}
                            sortable
                            style={{ minWidth: '170px' }}
                        />
                        {analysisMode === 'post_service' && (
                            <Column
                                header={appT('First report')}
                                body={(row) => (
                                    <div>
                                        <div>{formatDateTime(row.observed_at)}</div>
                                        <small className="text-color-secondary">
                                            {Number(row.minutes_after_event || 0).toFixed(1)} min
                                        </small>
                                    </div>
                                )}
                                field="observed_at"
                                sortable
                                style={{ minWidth: '180px' }}
                            />
                        )}
                        <Column header={appT('Evidence')} body={evidenceBody} style={{ minWidth: '210px' }} />
                    </DataTable>
                </div>
            )}
        </Card>
    );
};

export default CoinObservationsPanel;
